import React, { useState } from "react";
import { FiEdit2, FiTrash2 , FiX} from "react-icons/fi";
import { format } from "date-fns";

const DiaryList = ({ entries, onEdit, onDelete, pets }) => {
  const [previewImage, setPreviewImage] = useState(null);
  if (!entries || !entries.length) return null;

  const sortedEntries = [...entries].sort(
    (a, b) => new Date(`${b.date} ${b.time}`) - new Date(`${a.date} ${a.time}`)
  );

  const formatDateRow = (dateStr) => {
    const date = new Date(dateStr);
    const day = format(date, "EEE");
    const dayNum = format(date, "dd");
    const monthYear = format(date, "MMM yyyy");
    return { day, dayNum, monthYear };
  };

  return (
    <div className="py-5">
      
      {/* 1. PREVIEW MODAL */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setPreviewImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            onClick={() => setPreviewImage(null)}
          >
            <FiX size={32} />
          </button>
          <img 
            src={previewImage} 
            alt="Preview" 
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

      <div className="grid gap-6 md:gap-8">
        {sortedEntries.map((entry) => {
          const { day, dayNum, monthYear } = formatDateRow(entry.date);
          const isPublic = (entry.visibility || "").toLowerCase() === "public";
          return (
            <article
              key={entry._id || entry.id}
              className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              {/* Accent bar */}
              <div
                className={`absolute top-0 left-0 right-0 h-1 ${
                  isPublic
                    ? "bg-gradient-to-r from-emerald-400 to-teal-500"
                    : "bg-gradient-to-r from-purple-400 to-pink-500"
                }`}
              />

              <div className="p-5 sm:p-6 lg:p-8">
                {/* Header Section - Fully Responsive */}
                <div className="flex flex-col gap-5 mb-6">
                  {/* Date & Time - Stacks on mobile */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                    {/* Horizontal Date Row */}
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl px-5 py-1 shadow-lg flex flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold uppercase tracking-wider opacity-90">
                          {day}
                        </span>
                        <span className="text-2xl sm:text-3xl font-black">
                          {dayNum}
                        </span>
                      </div>
                      <div className="hidden sm:block h-12"></div>
                      <div className="sm:border-l sm:border-white/40 sm:pl-5">
                        <span className="text-base sm:text-lg font-medium opacity-95">
                          {monthYear}
                        </span>
                      </div>
                    </div>

                    {/* Time Badge */}
                    <div className="flex items-center gap-3 bg-gradient-to-r from-slate-100 to-slate-200 px-5 py-3 rounded-2xl border border-slate-300 shadow-sm self-start">
                      <span className="text-xl sm:text-2xl">⏰</span>
                      <span className="text-xl sm:text-2xl font-bold text-slate-800">
                        {entry.time}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons & Visibility - Wrap on small screens */}
                  <div className="flex flex-wrap items-center gap-3 justify-between sm:justify-end">
                    {/* Visibility Badge */}
                    <span
                      className={`inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold shadow-sm ${
                        isPublic
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                          : "bg-purple-100 text-purple-700 border border-purple-200"
                      }`}
                    >
                      {isPublic ? "🌍" : "🔒"} {entry.visibility}
                    </span>

                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit?.(entry)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium text-sm"
                        title="Edit"
                      >
                        <FiEdit2 size={16} />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => onDelete?.(entry._id)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium text-sm"
                        title="Delete"
                      >
                        <FiTrash2 size={16} />
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pet & Category Row */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {entry.petName && (
                    <div className="flex items-center gap-2 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-2 rounded-xl border border-orange-200">
                      <span className="text-xl">🐾</span>
                      <span className="text-base font-bold text-orange-700">
                        {entry.petName}
                      </span>
                    </div>
                  )}

                  <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-indigo-700 px-4 py-2 rounded-xl text-sm font-bold border border-indigo-200">
                    <span>📁</span>
                    {entry.category || "General"}
                  </span>
                </div>

                {/* Title */}
                {entry.title && (
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 leading-tight">
                    {entry.title}
                  </h3>
                )}

                {/* Notes */}
                <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-4 sm:p-5 mb-5 border border-gray-200">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {entry.notes}
                  </p>
                </div>

               {/* Media Grid */}
                {entry.media?.length > 0 && (
                  <div className="mb-5">
                    <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                      <span>📸</span> Media
                    </h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                      {entry.media.map((file, i) => {
                        const src = file?.url || (typeof file === "string" ? file : "");                        
                        // Check if it's an image
                        const isImage = file?.type?.startsWith("image") || 
                            (typeof src === "string" && /\.(jpg|jpeg|png|gif|webp)$/i.test(src));

                        if (isImage) {
                          return (
                            <div
                              key={i}
                              onClick={() => setPreviewImage(src)} // Trigger Preview
                              className="relative rounded-lg overflow-hidden shadow-sm hover:shadow-md cursor-zoom-in transition-all duration-200 aspect-square bg-gray-100 group/item"
                            >
                              <img
                                src={src}
                                alt="Media"
                                className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/20 transition-colors flex items-center justify-center">
                                <span className="text-white opacity-0 group-hover/item:opacity-100 transition-opacity text-xs font-bold bg-black/40 px-2 py-1 rounded">View</span>
                              </div>
                            </div>
                          );
                        }

                        // Video handling
                        return (
                          <div key={i} className="col-span-3 sm:col-span-2 rounded-lg overflow-hidden shadow-sm">
                            <video src={src} controls className="w-full h-32 object-cover bg-black" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {entry.documents?.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                      <span>📎</span> Attachments
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {entry.documents.map((doc, i) => {
                        const url = typeof doc === "string" ? doc : doc.url;
                        const name =
                          typeof doc === "string"
                            ? doc.split("/").pop()
                            : doc.name || `Document ${i + 1}`;

                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-3 rounded-xl transition-all duration-200 border border-blue-200 hover:border-blue-300 group"
                          >
                            <span className="text-2xl flex-shrink-0">📄</span>
                            <span className="truncate text-sm font-medium group-hover:underline">
                              {name}
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default DiaryList;
